import { isDefined } from 'ionic-angular/util/util';
import { isArray, isObject } from 'util';
import { AccessGroupDto } from '../../../vas/business-objects/access-group/access-group.dto';
import { AccountStatisticsDto } from '../../../vas/business-objects/account-statistics/account-statistics.dto';
import { AccountDto } from '../../../vas/business-objects/account/account.dto';
import { CategoryGroupDto } from '../../../vas/business-objects/category-group/category-group.dto';
import { ContactTypeDto } from '../../../vas/business-objects/contact-type/contact-type.dto';
import { ContactDto } from '../../../vas/business-objects/contact/contact.dto';
import { ControlTypeDto } from '../../../vas/business-objects/control-type/control-type.dto';
import { ControlDto } from '../../../vas/business-objects/control/control.dto';
import { DefaultTaskDto } from '../../../vas/business-objects/default-task/default-task.dto';
import { FieldDto } from '../../../vas/business-objects/field/field.dto';
import { FileDto } from '../../../vas/business-objects/file/file.dto';
import { FormDto } from '../../../vas/business-objects/form/form.dto';
import { GroupDto } from '../../../vas/business-objects/group/group.dto';
import { JobStatusDto } from '../../../vas/business-objects/job-status/job-status.dto';
import { JobTypeDto } from '../../../vas/business-objects/job-type/job-type.dto';
import { JobDto } from '../../../vas/business-objects/job/job.dto';
import { LookupTypeDto } from '../../../vas/business-objects/lookup-type/lookup-type.dto';
import { LookupDto } from '../../../vas/business-objects/lookup/lookup.dto';
import { MembershipDto } from '../../../vas/business-objects/membership/membership.dto';
import { NoteDto } from '../../../vas/business-objects/note/note.dto';
import { NotificationTemplateDto } from '../../../vas/business-objects/notification-template/notification-template.dto';
import { ReportLayoutDto } from '../../../vas/business-objects/report-layout/report-layout.dto';
import { ReportDto } from '../../../vas/business-objects/report/report.dto';
import { TaskTypeDto } from '../../../vas/business-objects/task-type/task-type.dto';
import { TaskDto } from '../../../vas/business-objects/task/task.dto';
import { TransitionDto } from '../../../vas/business-objects/transition/transition.dto';
import { UserDto } from '../../../vas/business-objects/user/user.dto';
import { isInArray } from '../../util/is-in-array';
import { isBlob, mergeDedupe } from '../../util/utils';
import { Dto } from './dto';

export interface IDiffOptions {
    refListProperties?: Array<string>;
    ignoreProperties?: Array<string>;
}

export abstract class DtoHelper {

    static dtoClassMap = {
        accessGroup: AccessGroupDto,
        account: AccountDto,
        accountStatistics: AccountStatisticsDto,
        categoryGroup: CategoryGroupDto,
        contact: ContactDto,
        contactType: ContactTypeDto,
        control: ControlDto,
        controlType: ControlTypeDto,
        defaultTask: DefaultTaskDto,
        field: FieldDto,
        file: FileDto,
        form: FormDto,
        group: GroupDto,
        job: JobDto,
        jobStatus: JobStatusDto,
        jobType: JobTypeDto,
        lookup: LookupDto,
        lookupType: LookupTypeDto,
        membership: MembershipDto,
        note: NoteDto,
        report: ReportDto,
        reportLayout: ReportLayoutDto,
        task: TaskDto,
        taskType: TaskTypeDto,
        transition: TransitionDto,
        user: UserDto,
        notificationTemplate: NotificationTemplateDto
    };

    static getDtoFromEntity(entity: string, data: object): Dto {

        if (!isDefined(DtoHelper.dtoClassMap[entity])) {
            throw Error(`failed to create dto instance. entity ${entity} not found`);
        }

        return new DtoHelper.dtoClassMap[entity](data);
    }

    static diff(a, b, options?: IDiffOptions): any {

        options = {
            ...{
                refListProperties: [],
                ignoreProperties: []
            } as IDiffOptions,
            ...options
        };

        const result = {};

        const keys = mergeDedupe(Object.keys(a), Object.keys(b));

        keys.forEach(key => {

            if (isInArray(key, options.ignoreProperties)) {
                return;
            }

            const aValue = a[key];
            const bValue = b[key];

            // if a and b are both null/undefined (0/0)
            if ((DtoHelper.isUndefinedOrNull(aValue) && DtoHelper.isUndefinedOrNull(bValue))) {
                return;
            }

            // if a is null/undefined and b is not null/undefined (0/1)
            if (DtoHelper.isUndefinedOrNull(aValue) && !DtoHelper.isUndefinedOrNull(bValue)) {
                result[key] = bValue;
                return;
            }

            // if a is not null/undefined and b is null/undefined (1/0)
            if (!DtoHelper.isUndefinedOrNull(aValue) && DtoHelper.isUndefinedOrNull(bValue)) {
                result[key] = bValue;
                return;
            }

            if (isBlob(aValue) || isBlob(bValue)) {
                result[key] = bValue;
                return;
            }

            if (isArray(aValue) && isInArray(key, options.refListProperties)) {

                const resultTmp = [];

                aValue.map(value => {
                    if (!isInArray(value, bValue)) {
                        resultTmp.push(`-${value}`);
                    }
                });

                bValue.map(value => {
                    if (!isInArray(value, aValue)) {
                        resultTmp.push(`+${value}`);
                    }
                });

                if (resultTmp.length) {
                    result[key] = resultTmp;
                }

                return;
            }

            if (isArray(aValue) || isObject(aValue)) {

                try {
                    if (JSON.stringify(aValue) !== JSON.stringify(bValue)) {
                        result[key] = bValue;
                        return;
                    }
                } catch (error) {
                    throw Error(`error during diff(). failed to stringify values: ${error.message}`);
                }
                return;
            }

            // if a is not null/undefined and b is not null/undefined and the values are different (1/1)
            if (!DtoHelper.isUndefinedOrNull(aValue) && !DtoHelper.isUndefinedOrNull(bValue) && aValue !== bValue) {
                result[key] = bValue;
                return;
            }

            return;
        });

        return result;

    }

    static isUndefinedOrNull(value): boolean {
        return !isDefined(value) || value === null;
    }
}



// WEBPACK FOOTER //
// ./src/core/store/dto/dto-helper.ts